import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, Edit2, Save, X, Check, FileJson, Table } from 'lucide-react';
import Button from './Button';
import { Card } from './Card';
import api from '../services/api';
import AdvancedJsonEditor from './AdvancedJsonEditor';

const KBConstructor = ({ projectId, onSave }) => {
  const [kbData, setKbData] = useState({ Items: [] });
  const [basicInfo, setBasicInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showBasicInfo, setShowBasicInfo] = useState(false);
  const [basicInfoViewMode, setBasicInfoViewMode] = useState('visual'); // 'visual' or 'json'
  const [kbViewMode, setKbViewMode] = useState('visual'); // 'visual' or 'json'
  const [basicInfoJson, setBasicInfoJson] = useState({});
  const [kbJson, setKbJson] = useState({ Items: [] });
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const itemRefs = useRef({});

  useEffect(() => {
    if (projectId) {
      loadKB();
    }
  }, [projectId]);

  useEffect(() => {
    // Auto-resize all textareas
    document.querySelectorAll('textarea[data-auto-resize]').forEach(textarea => {
      autoResize(textarea);
    });
  });

  useEffect(() => {
    if (pendingScrollId && itemRefs.current[pendingScrollId]) {
      itemRefs.current[pendingScrollId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollId(null);
    }
  }, [pendingScrollId, kbData]);

  const loadKB = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [kbResponse, basicInfoResponse] = await Promise.all([
        api.get(`/control_panel/${projectId}/kb`),
        api.get(`/control_panel/${projectId}/field/Basic_Info`)
      ]);
      const rawKb = kbResponse.data.data || {};
      // Normalize legacy lowercase keys to PascalCase
      const rawItems = Array.isArray(rawKb.Items) ? rawKb.Items
        : Array.isArray(rawKb.items) ? rawKb.items : [];
      const normalizedItems = rawItems.map((item, idx) => ({
        // Use || to treat empty strings as falsy; cover all known casing variants
        Request_ID: item.Request_ID || item.request_ID || item.request_id || item.ID || item.id || `item_${idx}`,
        Smart_Questions: Array.isArray(item.Smart_Questions) ? item.Smart_Questions
          : Array.isArray(item.smart_questions) ? item.smart_questions
          : Array.isArray(item.Smart_Questions_) ? item.Smart_Questions_ : [],
        Answers: (() => {
          const rawAnswers = item.Answers || item.answers || item.turn_to_answers || [];
          return Array.isArray(rawAnswers) ? rawAnswers.map(a =>
            typeof a === 'object' && a !== null
              ? { If: a.If ?? a.if ?? null, Answer: a.Answer ?? a.answer ?? '', File_Tags: a.File_Tags ?? a.file_tags ?? null }
              : { If: null, Answer: String(a), File_Tags: null }
          ) : [];
        })(),
        KB_Comment: item.KB_Comment || item.KB_comment || item.kb_comment || '',
      }));
      // Build normalized data, explicitly dropping legacy lowercase 'items' key
      const { items: _dropped, ...rawKbRest } = rawKb;
      const kbResponseData = { ...rawKbRest, Items: normalizedItems };
      setKbData(kbResponseData);
      setKbJson(kbResponseData);
      
      const basicInfoData = basicInfoResponse.data.data;
      // Store raw JSON for JSON editor
      setBasicInfoJson(basicInfoData || {});
      
      // Извлекаем значение Basic_Info и преобразуем для редактирования
      let basicInfoValue = '';
      if (basicInfoData && typeof basicInfoData === 'object' && 'Basic_Info' in basicInfoData) {
        const innerValue = basicInfoData.Basic_Info;
        if (Array.isArray(innerValue)) {
          // Если массив - каждый элемент с новой строки
          basicInfoValue = innerValue.join('\n');
        } else if (typeof innerValue === 'string') {
          basicInfoValue = innerValue;
        } else if (innerValue && typeof innerValue === 'object') {
          // Если объект - JSON.stringify с форматированием
          basicInfoValue = JSON.stringify(innerValue, null, 2);
        } else {
          basicInfoValue = '';
        }
      } else if (typeof basicInfoData === 'string') {
        basicInfoValue = basicInfoData;
      }
      setBasicInfo(basicInfoValue);
    } catch (error) {
      toast.error('Ошибка загрузки базы знаний');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveBasicInfo = async () => {
    try {
      let valueToSave;
      
      if (basicInfoViewMode === 'json') {
        // Save from JSON editor - replace entire field
        valueToSave = basicInfoJson;
      } else {
        // Преобразуем текст обратно в массив (строки разделены переносами)
        let innerValue;
        const lines = basicInfo.split('\n').filter(line => line.trim());
        
        // Если больше одной строки - сохраняем как массив
        if (lines.length > 1) {
          innerValue = lines;
        } else if (lines.length === 1) {
          // Одна строка - сохраняем как строку
          innerValue = lines[0];
        } else {
          innerValue = '';
        }
        
        // Сохраняем в структуре {Basic_Info: value}
        valueToSave = { Basic_Info: innerValue };
      }
      
      // Use PUT to fully replace the field (avoids nested 'Value' wrapping on each save)
      await api.put(`/control_panel/${projectId}/field/Basic_Info`, valueToSave);
      toast.success('Basic_Info сохранен');
      if (onSave) onSave();
    } catch (error) {
      toast.error('Ошибка сохранения Basic_Info');
      console.error(error);
    }
  };

  const saveKbJson = async () => {
    try {
      // Save entire KB from JSON editor
      await api.put(`/control_panel/${projectId}/kb`, kbJson);
      toast.success('База знаний сохранена');
      loadKB();
      if (onSave) onSave();
    } catch (error) {
      toast.error('Ошибка сохранения базы знаний');
      console.error(error);
    }
  };

  const autoResize = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
    }
  };

  const handleAddItem = async () => {
    const newItem = {
      Smart_Questions: ['Новый вопрос'],
      Answers: [{ If: null, Answer: 'Новый ответ', File_Tags: null }],
      Have_Tags: [],
      Not_Have_Tags: [],
      KB_Comment: ''
    };
    
    try {
      const response = await api.post(`/control_panel/${projectId}/kb/items`, {
        Smart_Questions: newItem.Smart_Questions,
        Answers: newItem.Answers,
        Have_Tags: newItem.Have_Tags,
        Not_Have_Tags: newItem.Not_Have_Tags,
        KB_Comment: newItem.KB_Comment
      });
      const newId = response.data?.data?.Request_ID;
      toast.success('Запись добавлена');
      await loadKB(true);
      if (newId) {
        setEditingItem(newId);
        setPendingScrollId(newId);
      }
      if (onSave) onSave();
    } catch (error) {
      toast.error('Ошибка создания записи');
      console.error(error);
    }
  };

  const handleUpdateItem = async (item) => {
    const filteredQuestions = item.Smart_Questions.filter(q => q && q.trim());
    const filteredAnswers = item.Answers.filter(a => a && a.Answer && a.Answer.trim());

    if (filteredQuestions.length === 0) {
      toast.error('Должен быть хотя бы один вопрос');
      return;
    }

    if (filteredAnswers.length === 0) {
      toast.error('Должен быть хотя бы один ответ');
      return;
    }

    // Проверка: если есть ответ с условием, должно быть минимум 2 ответа с условиями
    const answersWithConditions = filteredAnswers.filter(a => a.If !== null && a.If !== undefined && a.If.trim());
    if (answersWithConditions.length > 0 && answersWithConditions.length < 2) {
      toast.error('Если используются условия, должно быть минимум 2 ответа с условиями');
      return;
    }

    try {
      await api.put(`/control_panel/${projectId}/kb/items/${item.Request_ID}`, {
        Smart_Questions: filteredQuestions,
        Answers: filteredAnswers,
        Have_Tags: item.Have_Tags,
        Not_Have_Tags: item.Not_Have_Tags,
        KB_Comment: item.KB_Comment
      });
      toast.success('Запись обновлена');
      setEditingItem(null);
      loadKB();
      if (onSave) onSave();
    } catch (error) {
      toast.error('Ошибка обновления записи');
      console.error(error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Удалить эту запись?')) return;
    
    try {
      await api.delete(`/control_panel/${projectId}/kb/items/${itemId}`);
      toast.success('Запись удалена');
      loadKB();
      if (onSave) onSave();
    } catch (error) {
      toast.error('Ошибка удаления записи');
      console.error(error);
    }
  };

  const updateItemField = (itemId, field, value) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const addQuestion = (itemId) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId 
          ? { ...item, Smart_Questions: [...item.Smart_Questions, ''] }
          : item
      )
    }));
  };

  const updateQuestion = (itemId, index, value) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId
          ? {
              ...item,
              Smart_Questions: item.Smart_Questions.map((q, i) => i === index ? value : q)
            }
          : item
      )
    }));
  };

  const removeQuestion = (itemId, index) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId
          ? {
              ...item,
              Smart_Questions: item.Smart_Questions.filter((_, i) => i !== index)
            }
          : item
      )
    }));
  };

  const addAnswer = (itemId) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId
          ? {
              ...item,
              Answers: [...item.Answers, { If: null, Answer: '', File_Tags: null }]
            }
          : item
      )
    }));
  };

  const updateAnswer = (itemId, index, field, value) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId
          ? {
              ...item,
              Answers: item.Answers.map((a, i) => 
                i === index ? { ...a, [field]: value } : a
              )
            }
          : item
      )
    }));
  };

  const removeAnswer = (itemId, index) => {
    setKbData(prev => ({
      ...prev,
      Items: prev.Items.map(item => 
        item.Request_ID === itemId
          ? {
              ...item,
              Answers: item.Answers.filter((_, i) => i !== index)
            }
          : item
      )
    }));
  };

  const addConditionToAnswer = (itemId, answerIndex) => {
    setKbData(prev => {
      const updatedItems = prev.Items.map(item => {
        if (item.Request_ID !== itemId) return item;
        
        // Добавляем условие к текущему ответу
        const updatedAnswers = item.Answers.map((a, i) => 
          i === answerIndex ? { ...a, If: '' } : a
        );
        
        // Если это первое условие и ответ всего один - добавляем второй ответ с условием
        if (updatedAnswers.length === 1) {
          updatedAnswers.push({ If: '', Answer: '', File_Tags: null });
        }
        
        return { ...item, Answers: updatedAnswers };
      });
      
      return { ...prev, Items: updatedItems };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Загрузка базы знаний...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">База знаний</h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowBasicInfo(!showBasicInfo)}
            variant="secondary"
            size="sm"
          >
            {showBasicInfo ? 'Скрыть Basic Info' : 'Basic Info'}
          </Button>
          {showBasicInfo && (
            <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setBasicInfoViewMode('visual')}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  basicInfoViewMode === 'visual'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Table className="h-4 w-4 inline mr-1" />
                Текст
              </button>
              <button
                onClick={() => setBasicInfoViewMode('json')}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  basicInfoViewMode === 'json'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileJson className="h-4 w-4 inline mr-1" />
                JSON
              </button>
            </div>
          )}
          <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setKbViewMode('visual')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                kbViewMode === 'visual'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Table className="h-4 w-4 inline mr-1" />
              Таблица
            </button>
            <button
              onClick={() => setKbViewMode('json')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                kbViewMode === 'json'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileJson className="h-4 w-4 inline mr-1" />
              JSON
            </button>
          </div>
        </div>
      </div>

      {showBasicInfo && (
        <Card className="mb-4 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Basic Info</h3>
            <Button onClick={saveBasicInfo} size="sm">
              <Save size={14} className="mr-1" />
              Сохранить
            </Button>
          </div>
          {basicInfoViewMode === 'visual' ? (
            <>
              <textarea
                value={basicInfo}
                onChange={(e) => setBasicInfo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                rows={15}
                placeholder="Введите текст (каждая строка - отдельный элемент массива)..."
              />
              <div className="text-xs text-gray-500 mt-2">
                💡 Совет: Каждая строка будет отдельным элементом. Одна строка = текст, несколько строк = массив.
              </div>
            </>
          ) : (
            <AdvancedJsonEditor
              value={basicInfoJson}
              onChange={setBasicInfoJson}
              height="400px"
              enableSchemaValidation={false}
            />
          )}
        </Card>
      )}

      {kbViewMode === 'json' ? (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">База знаний (JSON)</h3>
            <Button onClick={saveKbJson} size="sm">
              <Save size={14} className="mr-1" />
              Сохранить
            </Button>
          </div>
          <AdvancedJsonEditor
            value={kbJson}
            onChange={setKbJson}
            height="600px"
            enableSchemaValidation={false}
          />
        </Card>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-12">#</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase" style={{width: '40%'}}>
                Вопросы
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase" style={{width: '50%'}}>
                Ответы
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-32">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {kbData.Items && kbData.Items.length > 0 ? (
              kbData.Items.map((item, index) => {
                const isEditing = editingItem === item.Request_ID;
                
                return (
                  <tr key={item.Request_ID} ref={el => itemRefs.current[item.Request_ID] = el} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{item.Request_ID}</td>
                    
                    <td className="px-4 py-3 align-top">
                      {isEditing ? (
                        <div className="space-y-2">
                          {(item.Smart_Questions || []).map((q, qi) => (
                            <div key={qi} className="flex gap-1">
                              <textarea
                                data-auto-resize
                                value={q}
                                onChange={(e) => {
                                  updateQuestion(item.Request_ID, qi, e.target.value);
                                  autoResize(e.target);
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none"
                                placeholder="Вопрос..."
                              />
                              {(item.Smart_Questions || []).length > 1 && (
                                <button
                                  onClick={() => removeQuestion(item.Request_ID, qi)}
                                  className="px-2 text-red-600 hover:text-red-700"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addQuestion(item.Request_ID)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Plus size={12} /> Вопрос
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {(item.Smart_Questions || []).map((q, i) => (
                            <div key={i} className="text-sm text-gray-900">{q}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    
                    <td className="px-4 py-3 align-top">
                      {isEditing ? (
                        <div className="space-y-2">
                          {item.Answers && item.Answers.map((answerObj, ai) => (
                            <div key={ai} className={`space-y-1 ${
                              (answerObj.If !== null && answerObj.If !== undefined)
                                ? 'border border-gray-200 rounded p-2'
                                : ''
                            }`}>
                              {(answerObj.If !== null && answerObj.If !== undefined) && (
                                <input
                                  type="text"
                                  value={answerObj.If || ''}
                                  onChange={(e) => updateAnswer(item.Request_ID, ai, 'If', e.target.value)}
                                  placeholder="Условие (If)..."
                                  className="w-full px-2 py-1 text-xs border border-blue-200 bg-blue-50 rounded focus:ring-1 focus:ring-blue-500"
                                />
                              )}
                              <div className="flex gap-1">
                                <textarea
                                  data-auto-resize
                                  value={answerObj.Answer || ''}
                                  onChange={(e) => {
                                    updateAnswer(item.Request_ID, ai, 'Answer', e.target.value);
                                    autoResize(e.target);
                                  }}
                                  placeholder="Ответ..."
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none"
                                />
                                {item.Answers.length > 1 && (
                                  <button
                                    onClick={() => removeAnswer(item.Request_ID, ai)}
                                    className="px-2 text-red-600 hover:text-red-700"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                              {!(answerObj.If !== null && answerObj.If !== undefined) && (
                                <button
                                  onClick={() => addConditionToAnswer(item.Request_ID, ai)}
                                  className="text-xs text-gray-500 hover:text-blue-600"
                                  title="Добавит условие (автоматически добавится второй ответ)"
                                >
                                  + условие
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addAnswer(item.Request_ID)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Plus size={12} /> Ответ
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {item.Answers && item.Answers.map((a, i) => (
                            <div key={i} className="bg-gray-50 px-2 py-1 rounded">
                              {a.If && (
                                <div className="text-xs text-blue-600 mb-1">⚡ {a.If}</div>
                              )}
                              <div className="text-sm text-gray-700">{a.Answer || a.answer || ''}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex flex-col gap-1">
                          <Button
                            onClick={() => handleUpdateItem(item)}
                            size="sm"
                            className="w-full"
                          >
                            <Check size={14} />
                          </Button>
                          <Button
                            onClick={() => setEditingItem(null)}
                            variant="secondary"
                            size="sm"
                            className="w-full"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            onClick={() => setEditingItem(item.Request_ID)}
                            size="sm"
                            variant="secondary"
                            title="Редактировать"
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteItem(item.Request_ID)}
                            size="sm"
                            variant="danger"
                            title="Удалить"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                  <div className="text-sm">База знаний пуста</div>
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-gray-200">
              <td colSpan="4" className="px-4 py-2">
                <button
                  onClick={handleAddItem}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded transition-colors"
                >
                  <Plus size={14} />
                  Добавить запись
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default KBConstructor;
